function titleFromGender(gender) {
  const g = String(gender || '').toLowerCase();
  if (g === 'male' || g === 'm') return 'Mr';
  if (g === 'female' || g === 'f') return 'Mrs';
  return '';
}

function joinAddress(parts) {
  return parts.filter((p) => p != null && String(p).trim() !== '').join(', ');
}

export function derivePatientCarrier(diagnoses) {
  if (!Array.isArray(diagnoses) || !diagnoses.length) return null;
  for (const d of diagnoses) {
    const status = d && d.affectedStatus;
    const basis = (d && d.carrierBasis) || {};
    if (status === 'carrier') {
      const established =
        (basis.obligate && basis.obligate.confirmed === true) || !!basis.tested;
      if (established) return { established: true, isCarrier: true };
    }
  }
  return null;
}

function variantItem(genotype, variant) {
  return {
    geneName: genotype.symbol || genotype.name || variant.gene || '',
    geneId: genotype.id || '',
    variantC: variant.variantC || '',
    variantG: variant.variantG || '',
    variantP: variant.variantP || '',
    labInterpretation: variant.labInterpretation || '',
    clinvarInterpretation: variant.clinvarInterpretation || '',
    carrierStatus: genotype.clinicallyConfirmed
      ? { established: true, isCarrier: true }
      : null,
    dateRecorded: genotype.dateRecorded || '',
    recordedAge: genotype.recordedAge,
    recordedBy: genotype.recordedBy || '',
    clinicallyConfirmed: !!genotype.clinicallyConfirmed,
    patientReported: !!genotype.patientReported,
    displayOnPedigree: !!genotype.displayOnPedigree
  };
}

/**
 * @param {object} record   the patient (User/UserChanged/Family) doc
 * @param {object} [contact] the contact record the letter is generated from
 * @param {string[]} [staffNames] resolved staff-member display names
 * @returns {{ values:object, collections:object }}
 */
export function buildResolveContext(
  record = {},
  contact = {},
  staffNames = []
) {
  const values = {
    // Identity
    familyId: record.familyId,
    individualId: record.familyMemberId || record.proBandId,
    upn: record.upn,
    // Demographics
    firstName: record.firstName,
    lastName: record.lastName,
    maidenName: record.maidenName,
    sex: record.gender || record.sex,
    genderIdentity: record.genderIdentity,
    pronouns: record.pronouns,
    ethnicity: Array.isArray(record.ethnicity)
      ? record.ethnicity.join(', ')
      : record.ethnicity,
    title: titleFromGender(record.gender || record.sex),
    dateOfBirth: {
      iso: record.dob,
      precision: record.dobPrecision,
      estimated: record.estimatedDob
    },
    dateOfDeath: {
      iso: record.dod,
      precision: record.dodPrecision,
      estimated: record.estimatedDod
    },
    age: {
      iso: record.dob,
      precision: record.dobPrecision,
      estimated: record.estimatedDob,
      isAlive: record.isAlive,
      dod: record.dod,
      dodPrecision: record.dodPrecision
    },
    // Contact details
    email: record.email,
    mobile: record.mobile,
    // Address (structured on User + a composed convenience token)
    addressLine1: record.address_line1,
    addressLine2: record.address_line2,
    addressCity: record.address_city,
    addressPostcode: record.address_post,
    address: joinAddress([
      record.address_line1,
      record.address_line2,
      record.address_city,
      record.address_post
    ]),
    // GP
    gpName: record.gpName,
    gpAddressLine1: record.gpAddress_line1,
    gpAddressLine2: record.gpAddress_line2,
    gpAddressCity: record.gpAddress_city,
    gpAddressPostcode: record.gpAddress_post,
    // Identifiers
    hospitalNumber: record.hospitalNumber,
    healthcareId: record.healthcareId,
    // Admin
    notes: record.familyNote || record.comments,
    // Clinical assertion (patient-level)
    carrierStatus: derivePatientCarrier(record.diagnoses),
    // Contact context
    contactType: contact.contactType,
    contactDate: contact.contactDate,
    contactTime: contact.contactTime,
    contactMode: contact.contactMode,
    contactStatus: contact.contactStatus,
    contactNotes: contact.contactNotes,
    staffMember:
      staffNames && staffNames.length ? staffNames.join(', ') : undefined,
    ageAtRecord: contact.ageAtRecord
  };

  const genotypes = Array.isArray(record.genotypes) ? record.genotypes : [];
  const variants = [];
  genotypes.forEach((g) => {
    const vs = Array.isArray(g.variants) ? g.variants : [];
    if (vs.length) vs.forEach((v) => variants.push(variantItem(g, v)));
    else variants.push(variantItem(g, {}));
  });

  const phenotypes = (
    Array.isArray(record.phenotypes) ? record.phenotypes : []
  ).map((p) => ({
    phenotypeName: p.name,
    phenotypeId: p.id,
    dateRecorded: p.dateRecorded,
    recordedAge: p.recordedAge,
    recordedBy: p.recordedBy,
    clinicallyConfirmed: !!p.clinicallyConfirmed,
    patientReported: !!p.patientReported,
    displayOnPedigree: !!p.displayOnPedigree
  }));

  const diagnoses = (
    Array.isArray(record.diagnoses) ? record.diagnoses : []
  ).map((d) => ({
    diseaseName: d.name,
    diseaseId: d.id,
    ageAtDiagnosis: d.age,
    dateReported: d.dateReported,
    recordedBy: d.recordedBy,
    clinicallyConfirmed: !!d.clinicallyConfirmed,
    isPatientReported: !!d.isPatientReported,
    displayOnPedigree: !!d.displayOnPedigree
  }));

  return {
    values,
    collections: {
      Variants: variants,
      Phenotypes: phenotypes,
      Diagnoses: diagnoses,
      Tests: []
    }
  };
}
